import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Check, ArrowRight, Loader2, Circle } from 'lucide-react';

const KEY = 'rr_preview';
const LADDER = [
  ['180\u00b0 review', 'We assess your product and evidence against what care providers need.'],
  ['360\u00b0 review', 'A wider look that brings in the people who use it day to day.'],
  ['Customer verification', 'We speak to your care provider customers to confirm the results.'],
  ['Road test', 'We use the product ourselves in a realistic care setting.'],
  ['Staff feedback', 'Front-line carers and managers tell us how it really works.'],
  ['Families & residents', 'Feedback from the people receiving care and their families.'],
];
const fmt = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-');

// Supplier portal: earn / deepen the "CTC independently reviewed" badge.
export default function PortalReviewCard({ api }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api().get('/supplier-listing/review-status', { params: { preview: sessionStorage.getItem(KEY) || undefined } })
      .then((r) => setD(r.data)).catch(() => setD({ visible: false }));
  }, [api]);
  if (!d || !d.visible) return null;

  const request = async () => {
    setBusy(true);
    try {
      await api().post('/supplier-listing/assurance-request', { kind: 'ctc_review', note: '' });
      setD({ ...d, requested: true });
      toast.success("Thanks - our team will be in touch to talk you through your options.");
    } catch { toast.error('Could not send request'); }
    finally { setBusy(false); }
  };
  const e = d.entry;
  const live = e && ['green', 'amber'].includes(e.status.key);
  const cta = d.requested
    ? <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-2 rounded-md" data-testid="portal-review-requested"><Check className="w-4 h-4" /> Review requested - we'll be in touch</span>
    : <button onClick={request} disabled={busy} data-testid="portal-request-review"
        className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-[#ffffff] font-semibold text-sm px-4 py-2 rounded-md disabled:opacity-60 transition-colors">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {live ? 'Request a deeper review' : e ? 'Request a re-review' : 'Request a review'} <ArrowRight className="w-4 h-4" />
      </button>;

  return (
    <div className="mt-4 bg-white border border-emerald-200 rounded-xl p-5" data-testid="portal-review-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50 border border-emerald-300 px-2.5 py-1 rounded-full"><ShieldCheck className="w-3.5 h-3.5" /> CTC independently reviewed</p>
          {live ? (
            <>
              <h3 className="font-heading font-bold text-gray-900 mt-2" data-testid="portal-review-live">Your badge is live: {e.ref}</h3>
              <p className="text-sm text-gray-500 mt-0.5">{e.solution_name} · {e.review_tool}° review · depth {e.depth} of 4 · next review {fmt(e.next_review_date)}{e.status.key === 'amber' ? ' (due soon)' : ''}</p>
            </>
          ) : (
            <>
              <h3 className="font-heading font-bold text-gray-900 mt-2" data-testid="portal-review-earn">{e ? 'Your review has lapsed - renew your badge' : 'Earn the CTC independently reviewed badge'}</h3>
              <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">Reviewed products show the badge on their supplier page, product pages and directory cards, and appear on our public Reviewed Products Register. Reviews are earned, never bought - our team will talk you through your options.</p>
            </>
          )}
        </div>
        {cta}
      </div>
      {live && d.missing.length > 0 && (
        <div className="mt-4 bg-emerald-50/60 border border-emerald-100 rounded-lg px-4 py-3" data-testid="portal-review-deeper">
          <p className="text-sm font-semibold text-gray-900">Go deeper to strengthen your badge</p>
          <ul className="mt-1.5 grid sm:grid-cols-2 gap-1">{d.missing.map((m) => <li key={m} className="text-sm text-gray-600 flex items-center gap-1.5"><Circle className="w-2.5 h-2.5 text-emerald-500" />{m}</li>)}</ul>
        </div>
      )}
      {!live && (
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="portal-review-ladder">
          {LADDER.map(([t, desc], i) => (
            <div key={t} className="border border-[#E5E7EB] rounded-lg px-3 py-2.5">
              <p className="text-sm font-semibold text-gray-900"><span className="text-emerald-600 mr-1">{i + 1}.</span>{t}</p>
              <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
